import React, { useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Flex, Box, Button, Text } from '@qga/roo-ui/components';
import { useDialog } from 'components/Dialog';
import Currency from 'components/Currency';
import Image from 'components/Image/Image';
import ErrorHeading from 'components/CheckoutPage/BookingErrorDialogs/ErrorHeading';
import { useDataLayer } from 'hooks/useDataLayer';
import { getAllAvailableOffers } from 'store/exclusiveOffer/exclusiveOfferSelectors';
import { getRoomTypesWithoutOffers } from 'store/property/propertySelectors';
import { MIN_POINTS_AMOUNT } from 'config';

interface MinimumPointsDialogProps {
  isOpen?: boolean;
  onContinueClick: () => void;
  onChangeClick: () => void;
  roomTypeId: string;
  offerId: string;
}

const MinimumPointsDialog = ({ isOpen, onContinueClick, onChangeClick, roomTypeId, offerId }: MinimumPointsDialogProps) => {
  const { emitInteractionEvent } = useDataLayer();
  const { Dialog, openDialog, closeDialog } = useDialog();

  const availableOffers = useSelector(getAllAvailableOffers);
  const roomTypes = useSelector(getRoomTypesWithoutOffers);
  const roomType = roomTypes.find((room) => room.id === roomTypeId);
  const offer = availableOffers[offerId];
  const quoteTotal = offer.charges.payableAtBooking.total;

  useEffect(() => {
    if (isOpen) {
      openDialog(true);
      emitInteractionEvent({ type: 'Booking Errors', value: 'Minimum Point Spend Error Showed' });
    } else {
      closeDialog(false);
    }
  }, [isOpen, openDialog, closeDialog, emitInteractionEvent]);

  const handleContinueClick = useCallback(() => {
    onContinueClick();
    emitInteractionEvent({ type: 'Booking Errors', value: 'Minimum Point Spend Accept Selected' });
  }, [emitInteractionEvent, onContinueClick]);

  const handleChangeClick = useCallback(() => {
    onChangeClick();
    emitInteractionEvent({ type: 'Booking Errors', value: 'Minimum Point Spend Change Selected' });
  }, [emitInteractionEvent, onChangeClick]);

  return (
    <Dialog aria-labelledby="minimum-points-heading">
      <ErrorHeading id="minimum-points-heading">Use a minimum of 5,000 points</ErrorHeading>
      <Box mb={[6, 8]}>
        <Text display="inline">
          The minimum amount when using points is 5,000 PTS. Please review and accept the booking total update, or change your selection.
        </Text>
      </Box>

      <Box border={1} borderRadius="default" borderColor="greys.alto" p={3} mb={8}>
        <Flex flexDirection={['column', 'row']}>
          <Box mr={[0, 4]} mb={[3, 0]}>
            {roomType?.mainImage && (
              <Image
                height={['72px', '100%']}
                width={['auto', '100px']}
                src={roomType.mainImage.urlMedium}
                alt={roomType.mainImage.caption}
              />
            )}
          </Box>
          <Box flex="1 1 auto">
            <Text as="p" mt={0} mb={2} fontSize={['base', 'md']} fontWeight="bold">
              {roomType?.name}
            </Text>

            <Box maxWidth="300px">
              <Flex alignContent="center" justifyContent="space-between">
                <Text fontSize={['sm', 'base']} fontWeight="bold">
                  New booking total:
                </Text>
                <Currency
                  amount={MIN_POINTS_AMOUNT.toNumber()}
                  currency="PTS"
                  lineHeight="loose"
                  fontSize={['sm', 'base']}
                  fontWeight="bold"
                />
              </Flex>
              <Flex alignContent="center" justifyContent="space-between" data-testid="prev-booking-total">
                <Text fontSize={['sm', 'base']}>Previous booking total:</Text>
                <Currency amount={quoteTotal.amount} currency={quoteTotal.currency} lineHeight="loose" fontSize={['sm', 'base']} />
              </Flex>
            </Box>
          </Box>
        </Flex>
      </Box>
      <Button variant="primary" width={['100%', 'auto']} mr={[0, 2]} onClick={handleContinueClick}>
        Accept and continue
      </Button>
      <Button variant="inverted" width={['100%', 'auto']} onClick={handleChangeClick}>
        Change selection
      </Button>
    </Dialog>
  );
};

export default MinimumPointsDialog;
